/**
 * Symptom-focus page content. Soft-language, no diagnosis, never prescriptive.
 *
 * Each topic produces a dedicated SEO landing page at `/focus/:slug` that:
 *   - Validates the visitor's experience (empathy)
 *   - Educates without naming conditions
 *   - Encourages a session as the calm next step
 *   - Auto-opens the Concierge with the topic pre-loaded as context
 *
 * The Concierge does NOT use the page's headline to recommend a therapist or
 * modality — see `concierge_routes.py` SYSTEM_PROMPT for the guardrails.
 */

export const FOCUS_TOPICS = {
  "lower-back-tension": {
    slug: "lower-back-tension",
    label: "Lower-back tension",
    seoTitle: "Lower-back tension — Ashlan Clinic, Bermuda",
    seoDescription:
      "Many of our clients begin their healing journey when lower-back tension keeps speaking up. Ashlan Clinic in Hamilton, Bermuda, offers calm, evidence-aware clinical massage therapy with experienced Therapists.",
    eyebrow: "Where many begin",
    headline: "When the lower back keeps speaking up",
    lead:
      "Lower-back tension is one of the most common reasons people first reach out to us. It often builds up quietly over weeks or months — until daily life starts narrowing around it. If that's where you are, you're not alone, and you're not exaggerating it.",
    image:
      "https://customer-assets.emergentagent.com/job_clinical-massage-bda/artifacts/d10ww7qm_DEEP%20TISSUE%20MASSAGE%207.png",
    imageAlt: "An Ashlan Clinic Therapist's hands during a clinical massage session focused on lower-back tension.",
    experienceTitle: "What our clients often describe",
    experience: [
      "A tightness that doesn't fully ease, even on rest days.",
      "Discomfort that shows up after sitting, driving, or standing too long.",
      "A reluctance to bend, twist, or lift the way you used to.",
      "Sleep that's interrupted by trying to find a comfortable position.",
      "A sense that 'something has been off' since a specific moment — or for so long it's hard to place.",
    ],
    approachTitle: "How our Therapists work",
    approach: [
      "First we listen — without rushing, without assuming.",
      "We work with what your body is showing in the room, not from a template.",
      "Sessions are evidence-aware and grounded in clinical massage therapy, never spa or quick-fix language.",
      "We work alongside any care you're already receiving from a physician, physiotherapist, or other provider — never instead of.",
    ],
    supportTitle: "Small things many find supportive between sessions",
    support: [
      "Slow, gentle movement — a five-minute walk after long sits often helps the lower back release.",
      "Warmth before bed — a warm bath or a heated pack on the lower back may help with rest.",
      "Hydration — back tissue holds water; many clients notice a difference when they drink consistently.",
      "Breathing slowly into the belly for two minutes before sleep — the nervous system often softens.",
    ],
    ctaTitle: "Whenever you're ready",
    ctaBody:
      "A session with one of our Therapists is a calm, evidence-aware first step. We'll listen, work together, and you'll always be in the driver's seat.",
    relatedTopics: ["neck-and-shoulder-tension", "sleep-support"],
  },

  "neck-and-shoulder-tension": {
    slug: "neck-and-shoulder-tension",
    label: "Neck & shoulder tension",
    seoTitle: "Neck & shoulder tension — Ashlan Clinic, Bermuda",
    seoDescription:
      "Many of us carry more in our neck and shoulders than we realise. Ashlan Clinic's experienced Therapists in Hamilton, Bermuda offer calm, attentive clinical massage therapy for tension that builds quietly.",
    eyebrow: "Tension that builds quietly",
    headline: "When the day ends in your shoulders",
    lead:
      "Neck and shoulder tightness is rarely about a single moment. It builds over hours at desks, on phones, in cars, in stress — and many people only notice it after the day has long ended. If that's familiar, we hear you.",
    image:
      "https://customer-assets.emergentagent.com/job_clinical-massage-bda/artifacts/hyr0rc80_HERO%20IMAGE%20-%20MASSAGE%203.png",
    imageAlt:
      "Clinical massage therapy focused on the neck and shoulder area at Ashlan Clinic, Bermuda.",
    experienceTitle: "What our clients often describe",
    experience: [
      "A dull, present tightness across the tops of the shoulders by mid-afternoon.",
      "Discomfort when turning the head or holding a fixed position.",
      "Tension that intensifies in stressful weeks and softens on calm ones.",
      "Headaches or eye strain that seem to come from the neck.",
      "A feeling that the body has 'forgotten how to relax' in that area.",
    ],
    approachTitle: "How our Therapists work",
    approach: [
      "We begin with listening — what builds the tension for you, when does it ease, how does your day shape it.",
      "Hands-on work is calm, precise, and adapted to what your body shows that session.",
      "We work alongside any other care you're already receiving — never instead of physician guidance.",
      "Many clients find that two or three sessions, spaced thoughtfully, give the body room to recalibrate.",
    ],
    supportTitle: "Small things many find supportive between sessions",
    support: [
      "Micro-breaks — every hour at a desk, two minutes of slow shoulder rolls and head turns.",
      "Screen height — eyes level with the top of the screen reduces forward head posture.",
      "Warmth — a heated pack across the upper back before sleep often helps people drop into rest.",
      "Slow exhale breathing — four-count in, six-count out, for two minutes. The shoulders often soften on their own.",
    ],
    ctaTitle: "Whenever you're ready",
    ctaBody:
      "A session is a calm conversation with your body, guided by one of our Therapists. We listen first, work together, and you stay in the driver's seat.",
    relatedTopics: ["lower-back-tension", "headache-support"],
  },

  "sleep-support": {
    slug: "sleep-support",
    label: "Sleep support",
    seoTitle: "Sleep support — Ashlan Clinic, Bermuda",
    seoDescription:
      "When sleep doesn't come easily, the body holds onto it. Ashlan Clinic in Hamilton, Bermuda, offers calm, evidence-aware bodywork that many clients find supportive for rest.",
    eyebrow: "When sleep doesn't come easily",
    headline: "When the body forgets how to rest",
    lead:
      "Sleep that doesn't come easily — or doesn't hold through the night — affects everything else. Many of our clients reach out for sessions not because of a specific pain, but because they want their nights back. That's a valid and complete reason to be here.",
    image:
      "https://customer-assets.emergentagent.com/job_clinical-massage-bda/artifacts/xhehak4l_REFLEXOLOGY%2027.png",
    imageAlt: "A reflexology session at Ashlan Clinic supporting sleep-related nervous-system regulation.",
    experienceTitle: "What our clients often describe",
    experience: [
      "Falling asleep takes longer than it used to.",
      "Waking up between 2am and 4am and struggling to drift back.",
      "Sleep that's there but unrestful — waking up not quite refreshed.",
      "A racing mind at bedtime even after a full day.",
      "Tension in the shoulders, jaw, or chest that softens only during the day.",
    ],
    approachTitle: "How our Therapists work",
    approach: [
      "Sleep is often about the nervous system more than the body itself.",
      "Sessions are calm, deliberately unhurried, and may help the body shift toward rest.",
      "We never make sleep promises — but many of our clients tell us they sleep differently in the days after a session.",
      "We're glad to work alongside any sleep guidance you're receiving from your physician.",
    ],
    supportTitle: "Small things many find supportive between sessions",
    support: [
      "Dimming household lights an hour before bed — the nervous system reads light as 'still awake'.",
      "A consistent wind-down routine, even just ten minutes, repeated nightly.",
      "Warmth in the body before bed — a warm shower or bath often helps people drop in.",
      "Slow exhale breathing — four in, six out, for two minutes. Repeated nightly, many find their nights soften.",
    ],
    ctaTitle: "Whenever you're ready",
    ctaBody:
      "A session is one calm step. Our Therapists listen first, work together with you, and you remain in the driver's seat throughout.",
    relatedTopics: ["headache-support", "neck-and-shoulder-tension"],
  },

  "headache-support": {
    slug: "headache-support",
    label: "Headache support",
    seoTitle: "Headache support — Ashlan Clinic, Bermuda",
    seoDescription:
      "When headaches become part of your week, Ashlan Clinic's experienced Therapists in Hamilton, Bermuda offer calm, attentive bodywork that many clients find supportive — never instead of physician care.",
    eyebrow: "When headaches start to repeat",
    headline: "When the pattern keeps coming back",
    lead:
      "Headaches that show up regularly — by the end of a workday, after sleep that didn't quite land, on stressful weeks — often live in patterns that build slowly. If yours have started repeating, you're not alone, and a calm conversation about it is a good first step.",
    image:
      "https://customer-assets.emergentagent.com/job_clinical-massage-bda/artifacts/hyr0rc80_HERO%20IMAGE%20-%20MASSAGE%203.png",
    imageAlt: "A clinical massage session at Ashlan Clinic focused on neck-and-shoulder release for headache support.",
    experienceTitle: "What our clients often describe",
    experience: [
      "A dull tightness that seems to start at the base of the skull.",
      "Pressure across the forehead or behind the eyes.",
      "Headaches that begin in the afternoon and build through the evening.",
      "Episodes that line up with stressful weeks, poor sleep, or long screen time.",
      "A pattern that's becoming hard to ignore.",
    ],
    approachTitle: "How our Therapists work",
    approach: [
      "We listen first — your patterns matter more than any general advice.",
      "Hands-on work is calm and adapted to what your body shows that session.",
      "We do not diagnose headaches. We work alongside any care you're receiving from your physician — never instead of.",
      "If anything you describe suggests medical attention is needed first, we'll say so plainly and gently.",
    ],
    supportTitle: "Small things many find supportive between sessions",
    support: [
      "Hydration — many clients notice a difference when they consistently drink water through the day.",
      "Regular meals — long gaps may trigger patterns for some people.",
      "Screen breaks — every hour, eyes off the screen for a minute or two.",
      "Slow exhale breathing — four in, six out, for two minutes at any moment of mounting tension.",
    ],
    ctaTitle: "Whenever you're ready",
    ctaBody:
      "If your headaches are new, severe, or accompanied by other symptoms, please consult your physician first. For ongoing patterns alongside that care, a calm session with one of our Therapists is a thoughtful next step.",
    relatedTopics: ["sleep-support", "neck-and-shoulder-tension"],
  },
};

export const FOCUS_LIST = Object.values(FOCUS_TOPICS);
